import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  adminListVoiceInquiries,
  adminVoiceInquiriesStats,
  adminUpdateVoiceInquiry,
  adminSendVoiceOffer,
} from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { Navigate } from "react-router-dom";
import {
  Loader2, Filter, Send, User, Mail, Phone, Save, X, Sparkles, Shield,
} from "lucide-react";
import { VOICES, VOICES_ORDER } from "../data/voices";

const STATUS_LABELS = {
  new:         { label: "Neu",           color: "#a48cff" },
  qualified:   { label: "Qualifiziert",  color: "#D4AF37" },
  offer_sent:  { label: "Angebot raus",  color: "#4B9DFF" },
  purchased:   { label: "Bezahlt",       color: "#22C55E" },
  rejected:    { label: "Abgelehnt",     color: "#EF4444" },
  archived:    { label: "Archiviert",    color: "#6B7280" },
};

const STATUS_ORDER = ["new", "qualified", "offer_sent", "purchased", "rejected", "archived"];

const AdminVoiceInquiries = () => {
  const { user, loading: authLoading } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterProduct, setFilterProduct] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState(null); // { mode: "detail" | "offer" }
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProduct) params.product_slug = filterProduct;
      if (filterStatus) params.status = filterStatus;
      const [list, s] = await Promise.all([
        adminListVoiceInquiries(params),
        adminVoiceInquiriesStats().catch(() => null),
      ]);
      setInquiries(list || []);
      setStats(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterProduct, filterStatus]);

  if (authLoading) return <div className="min-h-screen bg-[#050b1c] text-white pt-24 pb-16 flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const ADMIN_ROLES = ["admin", "SUPER_ADMIN", "ACADEMY_ADMIN", "CONTENT_EDITOR", "MENTOR_REVIEWER", "FINANCE_MANAGER", "SUPPORT_AGENT", "FACILITATOR"];
  if (!ADMIN_ROLES.includes(user?.role)) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#050b1c] text-white pt-24 pb-16" data-testid="admin-voice-inquiries">
      <Helmet>
        <title>Admin · SAT Stimmen™ Anfragen</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] mb-2">
              <Sparkles size={12} className="inline mr-1.5" /> Admin
            </div>
            <h1 className="serif text-4xl font-light">SAT Stimmen™ · Anfragen</h1>
            <p className="text-white/60 mt-2 text-sm">Anfrage-Pipeline: neu → qualifiziert → Angebot → bezahlt</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
            {STATUS_ORDER.map(st => {
              const s = STATUS_LABELS[st];
              const count = stats.by_status?.[st] || 0;
              return (
                <div key={st} className="p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`voice-stat-${st}`}>
                  <div className="text-[10px] tracking-widest uppercase" style={{ color: s.color }}>{s.label}</div>
                  <div className="serif text-3xl font-light mt-1">{count}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6 pb-6 border-b border-white/10">
          <Filter size={14} className="text-white/50" />
          <select
            data-testid="filter-product"
            value={filterProduct}
            onChange={e => setFilterProduct(e.target.value)}
            className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
          >
            <option value="">Alle Produkte</option>
            {VOICES_ORDER.map(s => (
              <option key={s} value={s}>{VOICES[s].label}</option>
            ))}
          </select>
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
          >
            <option value="">Alle Status</option>
            {STATUS_ORDER.map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
            ))}
          </select>
          <button
            onClick={load}
            data-testid="btn-refresh"
            className="ml-auto text-xs text-white/60 hover:text-white px-3 py-2 rounded-lg border border-white/10 hover:border-white/25"
          >
            Aktualisieren
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-24 text-center text-white/50"><Loader2 className="mx-auto mb-2 animate-spin" size={26} /> Lade …</div>
        ) : inquiries.length === 0 ? (
          <div className="py-24 text-center text-white/50">Keine Anfragen gefunden.</div>
        ) : (
          <div className="space-y-3">
            {inquiries.map(inq => {
              const v = VOICES[inq.product_slug];
              const st = STATUS_LABELS[inq.status] || STATUS_LABELS.new;
              return (
                <div
                  key={inq.id}
                  data-testid={`inquiry-row-${inq.id}`}
                  className="p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-white/20 transition cursor-pointer"
                  onClick={() => { setSelected(inq); setDrawer({ mode: "detail" }); }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                        <span className="text-[10px] tracking-[0.24em] uppercase px-2 py-0.5 rounded-full border" style={{ color: v?.accent || "#fff", borderColor: `${v?.accent || "#fff"}55` }}>
                          {v?.label || inq.product_slug}
                        </span>
                        <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ background: `${st.color}22`, color: st.color }}>
                          {st.label}
                        </span>
                        {inq.is_minor && (
                          <span className="text-[10px] tracking-widest uppercase text-amber-300 flex items-center gap-1">
                            <Shield size={10} /> Minderjährig
                          </span>
                        )}
                      </div>
                      <div className="serif text-lg text-white/95">
                        {inq.first_name} {inq.last_name || ""}
                      </div>
                      <div className="text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span className="inline-flex items-center gap-1"><Mail size={11} />{inq.email}</span>
                        {inq.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{inq.phone}</span>}
                        <span>{new Date(inq.created_at).toLocaleDateString("de-CH")}</span>
                      </div>
                      <p className="text-sm text-white/70 mt-2 line-clamp-2">{inq.motivation}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Detail Drawer */}
        {drawer && selected && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={() => { setDrawer(null); setSelected(null); }} data-testid="inquiry-drawer">
            <div
              className="w-full sm:max-w-xl bg-[#0b1428] border-l border-white/10 h-full overflow-y-auto p-6 sm:p-8"
              onClick={e => e.stopPropagation()}
            >
              <InquiryDetail
                inquiry={selected}
                onClose={() => { setDrawer(null); setSelected(null); }}
                onUpdated={(patch) => {
                  setSelected(prev => ({ ...prev, ...patch }));
                  setInquiries(prev => prev.map(i => i.id === selected.id ? { ...i, ...patch } : i));
                  if (patch.status) load();
                }}
                savingId={savingId}
                setSavingId={setSavingId}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InquiryDetail = ({ inquiry, onClose, onUpdated, savingId, setSavingId }) => {
  const v = VOICES[inquiry.product_slug];
  const [status, setStatus] = useState(inquiry.status || "new");
  const [notes, setNotes] = useState(inquiry.admin_notes || "");
  const [offerLink, setOfferLink] = useState(inquiry.offer_link || "");
  const [offerMessage, setOfferMessage] = useState("");

  const save = async () => {
    setSavingId(inquiry.id);
    try {
      await adminUpdateVoiceInquiry(inquiry.id, { status, admin_notes: notes, offer_link: offerLink });
      toast.success("Gespeichert");
      onUpdated({ status, admin_notes: notes, offer_link: offerLink });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  };

  const sendOffer = async () => {
    if (!offerLink || !offerLink.startsWith("http")) {
      toast.error("Bitte gültigen Stripe Payment Link einfügen.");
      return;
    }
    setSavingId(inquiry.id);
    try {
      await adminSendVoiceOffer(inquiry.id, { payment_link: offerLink, message: offerMessage });
      toast.success("Angebot per E-Mail versendet");
      onUpdated({ status: "offer_sent", offer_link: offerLink });
      setStatus("offer_sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase mb-2" style={{ color: v?.accent }}>{v?.label}</div>
          <h2 className="serif text-2xl font-light">{inquiry.first_name} {inquiry.last_name || ""}</h2>
          <div className="text-xs text-white/50 mt-1">{new Date(inquiry.created_at).toLocaleString("de-CH")}</div>
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="drawer-close"><X size={20} /></button>
      </div>

      <div className="space-y-1 text-sm mb-6">
        <div className="text-white/80"><Mail size={12} className="inline mr-2" />{inquiry.email}</div>
        {inquiry.phone && <div className="text-white/80"><Phone size={12} className="inline mr-2" />{inquiry.phone}</div>}
        <div className="text-white/60 text-xs">{inquiry.country}</div>
      </div>

      <section className="space-y-4 mb-8">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Motivation</div>
          <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">{inquiry.motivation}</p>
        </div>
        {inquiry.format_preference && (
          <div>
            <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Format-Wunsch</div>
            <p className="text-sm text-white/85">{inquiry.format_preference}</p>
          </div>
        )}
        {inquiry.availability && (
          <div>
            <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Verfügbarkeit</div>
            <p className="text-sm text-white/85">{inquiry.availability}</p>
          </div>
        )}
        {inquiry.additional_info && (
          <div>
            <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Zusatzinfo</div>
            <p className="text-sm text-white/85 whitespace-pre-wrap">{inquiry.additional_info}</p>
          </div>
        )}
        {inquiry.referral_source && (
          <div>
            <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Quelle</div>
            <p className="text-sm text-white/85">{inquiry.referral_source}</p>
          </div>
        )}
        {inquiry.is_minor && (
          <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
            <div className="text-[10px] tracking-widest uppercase text-amber-300 mb-2 flex items-center gap-1"><Shield size={11} /> Kinderschutz</div>
            <div className="text-sm text-white/85 space-y-1">
              <div>Alter: {inquiry.minor_age || "—"}</div>
              <div>Erziehungsberechtigt: {inquiry.guardian_name}</div>
              <div>{inquiry.guardian_email}</div>
              <div>Einwilligung: {inquiry.guardian_consent ? "✔ Ja" : "✘ Nein"}</div>
            </div>
          </div>
        )}
      </section>

      {/* Status & notes */}
      <section className="space-y-4 border-t border-white/10 pt-6 mb-8">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-white/40 mb-2">Status</div>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            data-testid="drawer-status"
            className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
          >
            {STATUS_ORDER.map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase text-white/40 mb-2">Admin-Notizen</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            data-testid="drawer-notes"
            className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm min-h-[90px]"
            placeholder="Interne Notizen …"
          />
        </div>
        <button
          onClick={save}
          disabled={savingId === inquiry.id}
          data-testid="drawer-save"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm border border-white/15 hover:border-white/30 disabled:opacity-50"
        >
          {savingId === inquiry.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
        </button>
      </section>

      {/* Offer send */}
      <section className="space-y-4 border-t border-white/10 pt-6">
        <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Angebot senden</div>
        <p className="text-xs text-white/50 leading-relaxed">
          Erzeuge einen Stripe Payment Link (z. B. via Stripe Dashboard oder aus dem Shop-Katalog)
          und füge ihn hier ein. Der Kunde erhält eine E-Mail mit persönlicher Ansprache und dem Link.
        </p>
        <input
          value={offerLink}
          onChange={e => setOfferLink(e.target.value)}
          data-testid="drawer-offer-link"
          placeholder="https://buy.stripe.com/..."
          className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
        />
        <textarea
          value={offerMessage}
          onChange={e => setOfferMessage(e.target.value)}
          data-testid="drawer-offer-message"
          placeholder="Optionale persönliche Nachricht (überschreibt Standard-Text)"
          className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm min-h-[80px]"
        />
        <button
          onClick={sendOffer}
          disabled={savingId === inquiry.id || !offerLink}
          data-testid="drawer-send-offer"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm bg-[#D4AF37] text-[#081328] font-medium disabled:opacity-50"
        >
          {savingId === inquiry.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Angebots-E-Mail senden
        </button>
      </section>
    </div>
  );
};

export default AdminVoiceInquiries;
